import React, { useState, useEffect } from 'react';
import { ImageIcon, Pencil, Trash2, Save, Loader2, Upload, UserPlus } from 'lucide-react';
import { useApi } from './DashboardLayout';
import { API } from '../App';

export default function LoginPageSettingsPage() {
  const { apiFetch } = useApi();
  const [texts, setTexts] = useState({
    logo_title: '', logo_subtitle: '', form_heading: '',
    hero_title: '', hero_subtitle: '', hero_description: '',
    secondary_pre_text: '', secondary_link_text: '', secondary_link_url: '',
    footer: '',
  });
  const [accessTexts, setAccessTexts] = useState({
    page_heading: '', info_title: '', info_body: '', info_important: '',
    submit_button: '', success_message: '', back_link: '',
  });
  const [bgPath, setBgPath] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savingAccess, setSavingAccess] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [msg, setMsg] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [accessLoaded, setAccessLoaded] = useState(false);

  useEffect(() => {
    apiFetch('/api/settings/login-page-texts').then(r => r?.json()).then(d => {
      if (d) setTexts(prev => ({ ...prev, ...d }));
      setLoaded(true);
    }).catch(() => setLoaded(true));
    apiFetch('/api/settings/request-access-texts').then(r => r?.json()).then(d => {
      if (d) setAccessTexts(prev => ({ ...prev, ...d }));
      setAccessLoaded(true);
    }).catch(() => setAccessLoaded(true));
    apiFetch('/api/settings/login-bg').then(r => r?.json()).then(d => {
      setBgPath(d?.path || null);
    }).catch(() => {});
  }, []);

  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(''), 3000); };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await apiFetch('/api/settings/login-page-texts', {
        method: 'PUT', body: JSON.stringify(texts),
      });
      const d = await res.json();
      flash(d.message || 'Gespeichert');
    } catch { flash('Fehler beim Speichern'); }
    setSaving(false);
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const token = localStorage.getItem('bgpr_token');
      const res = await fetch(`${API}/api/settings/login-bg`, {
        method: 'POST', body: fd,
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await res.json();
      if (d.path) setBgPath(d.path);
      flash(d.message || 'Bild hochgeladen');
    } catch { flash('Fehler beim Hochladen'); }
    setUploading(false);
    e.target.value = '';
  };

  const handleDeleteBg = async () => {
    if (!window.confirm('Hintergrundbild entfernen?')) return;
    await apiFetch('/api/settings/login-bg', { method: 'DELETE' });
    setBgPath(null);
    flash('Hintergrundbild entfernt');
  };

  const set = (k) => (e) => setTexts(prev => ({ ...prev, [k]: e.target.value }));
  const setA = (k) => (e) => setAccessTexts(prev => ({ ...prev, [k]: e.target.value }));

  const handleSaveAccess = async () => {
    setSavingAccess(true);
    try {
      const res = await apiFetch('/api/settings/request-access-texts', {
        method: 'PUT', body: JSON.stringify(accessTexts),
      });
      const d = await res.json();
      flash(d.message || 'Gespeichert');
    } catch { flash('Fehler beim Speichern'); }
    setSavingAccess(false);
  };

  return (
    <div data-testid="login-page-settings">
      <div className="page-header">
        <h2>Login-Seite</h2>
        <p>Hintergrundbild und Texte der Login-Seite konfigurieren</p>
      </div>

      {msg && (
        <div style={{ marginBottom: 16, padding: '10px 16px', borderRadius: 8, background: '#ECFDF5', color: '#065F46', fontSize: 13, fontWeight: 600 }} data-testid="login-settings-msg">
          {msg}
        </div>
      )}

      {/* ── Hintergrundbild ── */}
      <div className="card" style={{ marginBottom: '1.5rem' }} data-testid="login-bg-section">
        <div className="card-header">
          <h3>
            <span className="card-header-icon" style={{ background: 'rgba(59,130,246,0.1)', color: 'var(--info)' }}><ImageIcon size={18} /></span>
            Hintergrundbild der Login-Seite
          </h3>
        </div>
        <div className="card-body">
          <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>
            Wird auf großen Bildschirmen rechts neben dem Anmelde-Formular eingeblendet. Empfohlen: hochwertiges Foto, mindestens 1200 px breit.
            Das Bild wird automatisch auf max. 1600 px Kantenlänge skaliert und als JPEG optimiert.
          </p>

          {bgPath ? (
            <div style={{ marginBottom: 16, borderRadius: 8, overflow: 'hidden', border: '1px solid var(--border)', maxWidth: 560 }}>
              <img src={`${API}${bgPath}`} alt="Login-Hintergrund"
                style={{ width: '100%', height: 'auto', objectFit: 'cover', maxHeight: 280, display: 'block' }}
                data-testid="login-bg-preview" />
            </div>
          ) : (
            <div style={{ marginBottom: 16, borderRadius: 8, border: '2px dashed var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', fontSize: 13, maxWidth: 560, height: 140 }}>
              Kein Hintergrundbild konfiguriert
            </div>
          )}

          <div style={{ display: 'flex', gap: 10 }}>
            <label className="btn btn-primary" style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }} data-testid="login-bg-upload-btn">
              {uploading ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Upload size={14} />}
              {bgPath ? 'Bild ersetzen' : 'Bild hochladen'}
              <input type="file" accept="image/png,image/jpeg,image/webp" style={{ display: 'none' }} onChange={handleUpload} />
            </label>
            {bgPath && (
              <button className="btn btn-secondary" onClick={handleDeleteBg} data-testid="login-bg-delete-btn">
                <Trash2 size={14} /> Bild entfernen
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── Texte der Login-Seite ── */}
      <div className="card" style={{ marginBottom: '1.5rem' }} data-testid="login-texts-section">
        <div className="card-header">
          <h3>
            <span className="card-header-icon" style={{ background: 'rgba(59,130,246,0.1)', color: 'var(--info)' }}><Pencil size={18} /></span>
            Texte der Login-Seite
          </h3>
        </div>
        <div className="card-body">
          {!loaded ? <p>Laden...</p> : (
            <>
              <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 20 }}>
                Alle Texte können hier angepasst werden. Im Titel wird der Teil nach dem ersten „-" automatisch farblich hervorgehoben
                (z.&nbsp;B. „BGPR<b>-Akademie</b>"). Im Footer steht <code style={{ background: 'var(--page-bg)', padding: '2px 6px', borderRadius: 4, fontSize: 12 }}>{'{year}'}</code> für das aktuelle Jahr.
              </p>

              <div className="form-row">
                <div className="form-group">
                  <label>Logo-Titel (links oben)</label>
                  <input className="form-input" value={texts.logo_title} onChange={set('logo_title')} data-testid="input-logo-title" />
                </div>
                <div className="form-group">
                  <label>Untertitel (links oben, klein)</label>
                  <input className="form-input" value={texts.logo_subtitle} onChange={set('logo_subtitle')} data-testid="input-logo-subtitle" />
                </div>
              </div>

              <div className="form-group">
                <label>Überschrift über dem Formular</label>
                <input className="form-input" value={texts.form_heading} onChange={set('form_heading')} data-testid="input-form-heading" />
                <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>Wird mit rotem Unterstrich angezeigt (z.&nbsp;B. „Anmelden")</p>
              </div>

              <h4 style={{ fontSize: 14, fontWeight: 700, marginTop: 24, marginBottom: 12 }}>Rechte Hero-Spalte</h4>

              <div className="form-row">
                <div className="form-group">
                  <label>Große Überschrift (Hero)</label>
                  <input className="form-input" value={texts.hero_title} onChange={set('hero_title')} data-testid="input-hero-title" />
                </div>
                <div className="form-group">
                  <label>Kleine Überschrift unter der Hero-Überschrift</label>
                  <input className="form-input" value={texts.hero_subtitle} onChange={set('hero_subtitle')} data-testid="input-hero-subtitle" />
                </div>
              </div>
              <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: -8, marginBottom: 16 }}>Teil nach dem ersten „-" wird farblich hervorgehoben</p>

              <div className="form-group">
                <label>Untertitel-Absatz (Hero)</label>
                <textarea className="form-input" rows={3} value={texts.hero_description} onChange={set('hero_description')} data-testid="input-hero-description" />
              </div>

              <h4 style={{ fontSize: 14, fontWeight: 700, marginTop: 24, marginBottom: 12 }}>Sekundärer Link unter dem Anmelde-Button</h4>

              <div className="form-row">
                <div className="form-group">
                  <label>Vor-Text</label>
                  <input className="form-input" value={texts.secondary_pre_text} onChange={set('secondary_pre_text')} data-testid="input-secondary-pre" />
                </div>
                <div className="form-group">
                  <label>Link-Text</label>
                  <input className="form-input" value={texts.secondary_link_text} onChange={set('secondary_link_text')} data-testid="input-secondary-link" />
                </div>
              </div>

              <h4 style={{ fontSize: 14, fontWeight: 700, marginTop: 24, marginBottom: 12 }}>Footer</h4>

              <div className="form-group">
                <textarea className="form-input" rows={2} value={texts.footer} onChange={set('footer')} data-testid="input-footer" />
                <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                  Verwende <code style={{ background: 'var(--page-bg)', padding: '2px 6px', borderRadius: 4, fontSize: 12 }}>{'{year}'}</code> als Platzhalter für das aktuelle Jahr
                </p>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                <button className="btn btn-primary" onClick={handleSave} disabled={saving} data-testid="login-settings-save-btn">
                  {saving ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Save size={14} />}
                  {saving ? 'Speichern...' : 'Speichern'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── Texte der Zugangsanforderung ── */}
      <div className="card" style={{ marginBottom: '1.5rem' }} data-testid="access-texts-section">
        <div className="card-header">
          <h3>
            <span className="card-header-icon" style={{ background: 'rgba(16,185,129,0.1)', color: 'var(--success)' }}><UserPlus size={18} /></span>
            Texte der Seite „Zugang anfordern"
          </h3>
        </div>
        <div className="card-body">
          {!accessLoaded ? <p>Laden...</p> : (
            <>
              <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 20 }}>
                Konfigurieren Sie die Texte, die auf der Seite „Zugang anfordern" angezeigt werden.
              </p>

              <div className="form-group">
                <label>Seitenüberschrift</label>
                <input className="form-input" value={accessTexts.page_heading} onChange={setA('page_heading')} data-testid="input-access-heading" />
              </div>

              <h4 style={{ fontSize: 14, fontWeight: 700, marginTop: 24, marginBottom: 12 }}>Info-Box</h4>

              <div className="form-group">
                <label>Titel der Info-Box</label>
                <input className="form-input" value={accessTexts.info_title} onChange={setA('info_title')} data-testid="input-access-info-title" />
              </div>

              <div className="form-group">
                <label>Beschreibungstext</label>
                <textarea className="form-input" rows={4} value={accessTexts.info_body} onChange={setA('info_body')} data-testid="input-access-info-body" />
              </div>

              <div className="form-group">
                <label>Hinweistext „Wichtig:"</label>
                <input className="form-input" value={accessTexts.info_important} onChange={setA('info_important')} data-testid="input-access-important" />
              </div>

              <h4 style={{ fontSize: 14, fontWeight: 700, marginTop: 24, marginBottom: 12 }}>Buttons und Links</h4>

              <div className="form-row">
                <div className="form-group">
                  <label>Text des Absende-Buttons</label>
                  <input className="form-input" value={accessTexts.submit_button} onChange={setA('submit_button')} data-testid="input-access-submit" />
                </div>
                <div className="form-group">
                  <label>Text des Zurück-Links</label>
                  <input className="form-input" value={accessTexts.back_link} onChange={setA('back_link')} data-testid="input-access-back" />
                </div>
              </div>

              <h4 style={{ fontSize: 14, fontWeight: 700, marginTop: 24, marginBottom: 12 }}>Erfolgsmeldung</h4>

              <div className="form-group">
                <label>Meldung nach erfolgreicher Absendung</label>
                <textarea className="form-input" rows={3} value={accessTexts.success_message} onChange={setA('success_message')} data-testid="input-access-success" />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                <button className="btn btn-primary" onClick={handleSaveAccess} disabled={savingAccess} data-testid="access-settings-save-btn">
                  {savingAccess ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Save size={14} />}
                  {savingAccess ? 'Speichern...' : 'Speichern'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
